/**
 * Report-Definition für den Projektauftrag (lange Variante).
 *
 * Übersetzt den gespeicherten Datenstand in ein {@link ReportDocument}. Diese
 * Datei ist die Vorlage für den globalen PDF-Rollout: jede Seite bekommt eine
 * analoge build*-Funktion, die nur ihren Inhalt deklariert.
 */
import {
  buildDocument,
  buildSection,
  keyValuesBlock,
  kv,
  tableBlock,
  textBlock,
  type ReportBlock,
  type ReportDocument,
} from '../../../lib/pdf/reportModel';

export type Axis = 'scope' | 'cost' | 'schedule';
type StaceyZone = 'simple' | 'complicated' | 'complex' | 'chaotic';

export interface ProjektauftragReportInput {
  // Auftrag & Organisation
  auftraggeber: string;
  sponsor: string;
  vertrag: string;
  rahmenbedingungen: string;
  // Ziele
  zieleHaupt: string;
  zieleTeil: string;
  zieleNicht: string;
  zieleAkzeptanz: string;
  bekannteUnbekannte: string;
  // Team
  team: { name: string; rolle: string | null; funktion: string | null; email: string | null }[];
  // Zeiten
  zeitenStart: string | null;
  zeitenEnde: string | null;
  zeitenPflichttermine: string;
  zeitenBeschraenkungen: string;
  // Projektsteuerung
  fixed: Axis[];
  staceyX: number;
  staceyY: number;
}

const AXIS_LABEL: Record<Axis, string> = {
  scope: 'Scope',
  cost: 'Kosten',
  schedule: 'Zeit',
};
const ALL_AXES: Axis[] = ['scope', 'cost', 'schedule'];

// Identisch zum ProjektauftragTab-Grid: y oben (0) -> unten (3), x links (0) -> rechts (3).
const ZONE_GRID: StaceyZone[][] = [
  ['complex', 'complex', 'chaotic', 'chaotic'],
  ['complicated', 'complex', 'complex', 'chaotic'],
  ['complicated', 'complicated', 'complex', 'complex'],
  ['simple', 'complicated', 'complicated', 'complex'],
];

const ZONE_LABEL: Record<StaceyZone, string> = {
  simple: 'Simple',
  complicated: 'Complicated',
  complex: 'Complex',
  chaotic: 'Chaotic',
};

const ZONE_DESC: Record<StaceyZone, string> = {
  simple: 'Klassisches Vorgehen, plan-getrieben (Wasserfall).',
  complicated: 'Experten-getrieben, klassisches PM mit Lenkungskreis.',
  complex: 'Agil – Scrum, Kanban, kurze Lernzyklen, MVP-Ansatz.',
  chaotic: 'Krisenmodus – erst stabilisieren, dann strukturieren.',
};

function clampIdx(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(3, Math.round(n)));
}

/** 'YYYY-MM-DD' -> 'DD.MM.YYYY'; leere/ungültige Werte -> ''. */
function formatDate(iso: string | null): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

export function buildProjektauftragReport(input: ProjektauftragReportInput): ReportDocument {
  const x = clampIdx(input.staceyX);
  const y = clampIdx(input.staceyY);
  const zone = ZONE_GRID[y]![x]!;

  const fixed = input.fixed.filter((a) => ALL_AXES.includes(a));
  const flex = ALL_AXES.filter((a) => !fixed.includes(a));

  const teamBlock: ReportBlock =
    input.team.length > 0
      ? tableBlock(
          [
            { header: 'Name', weight: 2.2 },
            { header: 'Funktion', weight: 1.8 },
            { header: 'Rolle', weight: 1.6 },
            { header: 'E-Mail', weight: 2.4 },
          ],
          input.team.map((m) => [m.name || '—', m.funktion || '—', m.rolle || '—', m.email || '—']),
        )!
      : { kind: 'note', value: 'Noch kein Team erfasst.' };

  return buildDocument([
    buildSection('Auftrag & Organisation', [
      keyValuesBlock([
        kv('Auftraggeber / Sponsor', input.auftraggeber),
        kv('Kunde', input.sponsor),
        kv('Vertragsbedingungen / Auftragstyp', input.vertrag),
        kv('Sonstige Rahmenbedingungen', input.rahmenbedingungen),
      ]),
    ]),
    buildSection('Ziele', [
      textBlock('Hauptziel', input.zieleHaupt),
      textBlock('Meilensteine', input.zieleTeil),
      textBlock('Nicht-Ziele', input.zieleNicht),
      textBlock('Akzeptanzkriterien', input.zieleAkzeptanz),
      textBlock('Bekannte Unbekannte / Risiken', input.bekannteUnbekannte),
    ]),
    { title: 'Projekt-Team', blocks: [teamBlock] },
    buildSection('Zeiten', [
      keyValuesBlock([
        kv('Frühester Projektstart', formatDate(input.zeitenStart)),
        kv('Spätestes Projektende', formatDate(input.zeitenEnde)),
      ]),
      textBlock('Pflichttermine', input.zeitenPflichttermine),
      textBlock('Beschränkungen', input.zeitenBeschraenkungen),
    ]),
    buildSection('Projektsteuerung', [
      keyValuesBlock([
        kv('Fixierte Achsen (Magisches Dreieck)', fixed.length ? fixed.map((a) => AXIS_LABEL[a]).join(' + ') : '—'),
        kv('Stellschraube', flex.length ? flex.map((a) => AXIS_LABEL[a]).join(', ') : '—'),
        kv('Stacey-Einordnung', ZONE_LABEL[zone]),
      ]),
      textBlock('Empfohlenes Vorgehen', ZONE_DESC[zone]),
    ]),
  ]);
}
